import { z } from 'zod';
import { JIRA_MAPPABLE_STATUSES, productTypeSchema, } from './domain.js';
/**
 * Contracts of the administration (screens 10, 11, 12 and 14), PO only. No
 * contract carries a writable role: roles come from Entra (ADR-002) and are
 * only displayed.
 */
const name = z.string().trim().min(1, 'Le nom est obligatoire.').max(120);
export const teamSaveSchema = z
    .object({
    name,
    membersSeeTeamRequests: z.boolean().default(false),
    productIds: z.array(z.uuid()).max(100),
    memberIds: z.array(z.uuid()).max(500),
})
    .strict();
// ---------------------------------------------------------------------------
// 11 · Utilisateurs & accès
// ---------------------------------------------------------------------------
export const NO_TEAM_FILTER = 'none';
export const usersQuerySchema = z
    .object({
    q: z.string().trim().max(100).optional(),
    role: z.enum(['requester', 'referent', 'po']).optional(),
    teamId: z.union([z.uuid(), z.literal(NO_TEAM_FILTER)]).optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
})
    .strict();
export const attachmentSchema = z.object({ teamId: z.uuid() }).strict();
/** « Créer un nouvel utilisateur » (ADR-014): the account is linked to Entra at its first login, by this address. */
export const userInviteSchema = z
    .object({
    firstName: z.string().trim().min(1, 'Le prénom est obligatoire.').max(100),
    lastName: z.string().trim().min(1, 'Le nom est obligatoire.').max(100),
    email: z.string().trim().toLowerCase().email('Adresse email invalide.').max(254),
    teamIds: z.array(z.uuid()).min(1, 'Choisissez au moins une équipe.').max(20),
})
    .strict();
export const productSaveSchema = z
    .object({
    name,
    type: productTypeSchema,
    /** POs of the product (ADR-010): any number, no duplicate. */
    ownerIds: z
        .array(z.uuid())
        .max(20)
        .refine((ids) => new Set(ids).size === ids.length, 'Une personne apparaît deux fois.'),
    isActive: z.boolean(),
    jiraBoardId: z.number().int().positive().nullable(),
    /** ADR-015: a Monday board and its status column, alongside or instead of a Jira board. */
    mondayBoardId: z.string().regex(/^\d+$/).nullable().default(null),
    mondayStatusColumnId: z.string().trim().min(1).max(100).nullable().default(null),
    requiresCmsArticleUrl: z.boolean(),
    requiresClientInfo: z.boolean(),
})
    .strict();
export const jiraMappingSaveSchema = z
    .object({
    mappings: z
        .array(z
        .object({ jiraStatus: z.string().trim().min(1).max(100), portalStatus: z.enum(JIRA_MAPPABLE_STATUSES).nullable() })
        .strict())
        .max(100),
})
    .strict();
// ----- Required Jira fields of a product (ADR-013) ---------------------------
export const JIRA_FIELD_KINDS = ['labels', 'text', 'option', 'options', 'user', 'users', 'unsupported'];
export const jiraPersonSchema = z.object({ accountId: z.string().min(1).max(128), displayName: z.string().max(200) }).strict();
export const productJiraFieldsSaveSchema = z
    .object({
    fields: z
        .array(z
        .object({
        fieldId: z.string().trim().min(1).max(100),
        // A Jira label cannot contain a space.
        values: z.array(z.string().trim().min(1).max(255)).max(20),
        people: z.array(jiraPersonSchema).max(20),
    })
        .strict())
        .max(50),
})
    .strict();
// ---------------------------------------------------------------------------
// 14 · Journal d'audit
// ---------------------------------------------------------------------------
/** Filter groups of screen 14 (« Action »), each covering one or more audit actions. */
export const AUDIT_ACTION_GROUPS = [
    'qualification',
    'attachment',
    'team',
    'product',
    'jira_mapping',
    'jira_sync',
    'backlog',
    'release_note',
];
export const AUDIT_PERIODS = ['7d', '30d', 'custom'];
/** Actor filter value for « Système » (Jira). */
export const SYSTEM_ACTOR = 'system';
export const auditQuerySchema = z
    .object({
    actorId: z.union([z.uuid(), z.literal(SYSTEM_ACTOR)]).optional(),
    group: z.enum(AUDIT_ACTION_GROUPS).optional(),
    period: z.enum(AUDIT_PERIODS).default('7d'),
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    q: z.string().trim().max(100).optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(8),
})
    .strict();
//# sourceMappingURL=admin.js.map